import { toTimestamp } from '../utils/addWebSocketToReplication'
import { toISOString, toISOStringOrNull } from '../utils/pushSafetyUtils'
import { createReplication } from './createReplication'
import type { ProjectCollection } from '../index'

const VISIBILITY_MAP: Record<number, 'private' | 'viewOnly' | 'open'> = {
  0: 'private',
  1: 'viewOnly',
  2: 'open',
}

const VISIBILITY_INT_MAP: Record<string, number> = {
  private: 0,
  viewOnly: 1,
  open: 2,
}

function toVisibilityString(v: number | null | undefined): 'private' | 'viewOnly' | 'open' {
  return VISIBILITY_MAP[v ?? 0] ?? 'private'
}

function fromVisibilityString(v: string | null | undefined): number {
  return VISIBILITY_INT_MAP[v ?? 'private'] ?? 0
}

export const setupProjectReplication = (
  collection: ProjectCollection,
  envId: string,
) =>
  createReplication({
    entityName: 'projects',
    displayName: 'Project',
    collection,
    envId,
    mapFromServer: (doc) => ({
      id: doc.id,
      envId: doc.envId,
      projectKey: doc.projectKey,
      name: doc.name,
      icon: doc.icon,
      iconType: doc.iconType || null,
      iconColor: doc.iconColor || null,
      parentProjectId: doc.parentProjectId || null,
      startDate: toTimestamp(doc.startDate),
      endDate: toTimestamp(doc.endDate),
      ownerId: doc.ownerId || null,
      description: doc.description || null,
      visibility: toVisibilityString(doc.visibility),
      sortOrder: doc.sortOrder ?? null,
      estimationType: doc.estimationType ?? null,
      isAccountableEnabled: doc.isAccountableEnabled ?? false,
      isWorkItemTypeEnabled: doc.isWorkItemTypeEnabled ?? false,
      statusMode: doc.statusMode ?? null,
      statusMigrationInProgress: doc.statusMigrationInProgress ?? false,
      superAgentEnabled: doc.superAgentEnabled ?? false,
      isCyclesEnabled: doc.isCyclesEnabled ?? false,
      cycleDurationWeeks: doc.cycleDurationWeeks ?? null,
      coverImageUrl: doc.coverImageUrl || null,
      coverImageType: doc.coverImageType || null,
      coverImageMeta: doc.coverImageMeta || null,
      isDeleted: doc.isDeleted || false,
      createdOn: toTimestamp(doc.createdOn) ?? new Date(doc.createdOn).getTime(),
      createdBy: doc.createdBy,
      updatedOn: toTimestamp(doc.updatedOn) ?? new Date(doc.updatedOn).getTime(),
      updatedBy: doc.updatedBy,
      deletedOn: doc.deletedOn ? (toTimestamp(doc.deletedOn) ?? new Date(doc.deletedOn).getTime()) : null,
      deletedBy: doc.deletedBy,
    }),
    mapToServer: (state, envId) => ({
      id: state.id,
      envId,
      projectKey: state.projectKey,
      name: state.name,
      icon: state.icon,
      iconType: state.iconType,
      iconColor: state.iconColor,
      parentProjectId: state.parentProjectId,
      startDate: toISOStringOrNull(state.startDate),
      endDate: toISOStringOrNull(state.endDate),
      ownerId: state.ownerId,
      description: state.description || null,
      visibility: fromVisibilityString(state.visibility),
      sortOrder: state.sortOrder ?? null,
      estimationType: state.estimationType ?? null,
      isAccountableEnabled: state.isAccountableEnabled ?? false,
      isWorkItemTypeEnabled: state.isWorkItemTypeEnabled ?? false,
      statusMode: state.statusMode ?? null,
      statusMigrationInProgress: state.statusMigrationInProgress ?? false,
      // Server-authoritative (set by the Super Agent onboard/offboard endpoints); echoed
      // back so the payload shape stays complete. The push handler ignores it.
      superAgentEnabled: state.superAgentEnabled ?? false,
      isCyclesEnabled: state.isCyclesEnabled ?? false,
      cycleDurationWeeks: state.cycleDurationWeeks ?? null,
      coverImageUrl: state.coverImageUrl,
      coverImageType: state.coverImageType,
      coverImageMeta: state.coverImageMeta,
      isDeleted: state.isDeleted,
      createdOn: toISOString(state.createdOn),
      createdBy: state.createdBy,
      updatedOn: toISOString(state.updatedOn),
      updatedBy: state.updatedBy,
      deletedOn: toISOStringOrNull(state.deletedOn),
      deletedBy: state.deletedBy,
    }),
  })
